/**
 * Assign redemption codes to a user.
 *
 * Assignment is admin-only — there is no self-service queue, because Apple gives a
 * finite number of custom-app codes and someone has to decide who gets one.
 *
 * The user picker is the same `userService.search()` typeahead the task Looped-In drawer
 * uses. It also supplies the {userId, name, email} snapshot the backend stores: the
 * catalogue service owns its own database and never reads Identity's, so the client
 * forwards the display fields it already has.
 */

import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IconSearch, IconUserPlus, IconTicket, IconAlertTriangle } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  TextInput,
  NumberInput,
  Textarea,
  Alert,
  Avatar,
  Card,
  Loader,
  toast,
} from '@/components/ui';
import { Modal } from '@/components/ui/overlays';
import { useUserService, useAppDistributionService } from '@actschurch/ccms-sdk';

interface AssignCodesModalProps {
  opened: boolean;
  onClose: () => void;
  distributionId: number;
  targetLabel: string;
  /** Codes currently handable-out. 0 means the pool is empty. */
  available: number;
  onAssigned: () => void;
}

interface PickedUser {
  userId: string;
  displayName: string;
  email: string;
}

export default function AssignCodesModal({
  opened,
  onClose,
  distributionId,
  targetLabel,
  available,
  onAssigned,
}: AssignCodesModalProps) {
  const userService = useUserService();
  const distributionService = useAppDistributionService();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [picked, setPicked] = useState<PickedUser | null>(null);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['users', 'search', 'code-assign', debouncedSearch],
    queryFn: async () => {
      const users: any[] = await userService.search({
        searchTerm: debouncedSearch,
        page: 1,
        pageSize: 25,
      } as any);
      return users;
    },
    enabled: opened && debouncedSearch.length >= 2,
    staleTime: 60 * 1000,
  });

  const assignMutation = useMutation({
    mutationFn: () =>
      distributionService.assignCodes(distributionId, {
        assignee: {
          userId: picked!.userId,
          displayName: picked!.displayName || null,
          email: picked!.email || null,
        },
        count,
        note: note.trim() || null,
      }),
    onSuccess: (codes) => {
      if (codes.length === 0) {
        // Belt-and-braces: the API answers 409 when the pool is empty, so we
        // normally never land here.
        toast.error('No codes left in the pool', 'Upload more before assigning.');
        return;
      }
      if (codes.length < count) {
        toast.success(
          `${codes.length} of ${count} code(s) assigned`,
          'The pool ran out — upload more codes.'
        );
      } else {
        toast.success(
          `${codes.length} code(s) assigned to ${picked!.displayName || picked!.email}`,
          'They have been emailed the code and the install steps.'
        );
      }
      reset();
      onAssigned();
      onClose();
    },
    onError: (err: any) => {
      const message: string = err?.message ?? '';
      if (/pool|409/i.test(message)) {
        toast.error('No codes left in the pool', 'Upload more before assigning.');
      } else {
        toast.error('Failed to assign', message);
      }
    },
  });

  const reset = () => {
    setSearch('');
    setDebouncedSearch('');
    setPicked(null);
    setCount(1);
    setNote('');
  };

  const poolEmpty = available === 0;

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={`Assign a code — ${targetLabel}`}
      size="lg"
    >
      <Stack gap="md">
        {poolEmpty ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              There are no codes left to hand out. Upload a new batch before assigning.
            </Text>
          </Alert>
        ) : (
          <Alert color="violet" icon={<IconTicket size={16} />}>
            <Text size="sm">
              {available} code(s) available. The person you pick gets an email with the code
              and the install steps, and it appears on their Apps page straight away.
            </Text>
          </Alert>
        )}

        {picked ? (
          <Card withBorder padding="sm" radius="md">
            <Group justify="space-between">
              <Group gap="sm">
                <Avatar radius="xl" size="md" color="violet">
                  {(picked.displayName || picked.email || '?').charAt(0).toUpperCase()}
                </Avatar>
                <div>
                  <Text fw={600} size="sm">
                    {picked.displayName || '(no name)'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {picked.email || 'No email on file — they will not get the email, only the Apps page.'}
                  </Text>
                </div>
              </Group>
              <Button variant="subtle" size="xs" onClick={() => setPicked(null)}>
                Change
              </Button>
            </Group>
          </Card>
        ) : (
          <>
            <TextInput
              label="Who is it for?"
              placeholder="Type at least 2 characters to search…"
              leftSection={<IconSearch size={16} />}
              rightSection={isFetching ? <Loader size="xs" /> : null}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />

            {debouncedSearch.length >= 2 && results.length === 0 && !isFetching && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No matching users
              </Text>
            )}

            <Stack gap={4}>
              {results.map((u: any) => {
                const userId = u.id || u.userId || u.userID || '';
                const displayName =
                  u.fullName ||
                  [u.firstName, u.surname ?? u.lastName].filter(Boolean).join(' ') ||
                  u.userName ||
                  '';
                const email = u.email || '';
                if (!userId) return null;

                return (
                  <Card
                    key={userId}
                    withBorder
                    padding="xs"
                    radius="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPicked({ userId, displayName, email })}
                  >
                    <Group gap="sm">
                      <Avatar radius="xl" size="sm" color="violet">
                        {(displayName || email || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {displayName || '(no name)'}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {email}
                        </Text>
                      </div>
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}

        {picked && (
          <>
            <NumberInput
              label="How many codes?"
              description="More than one when they need a second device, or lost the first code."
              value={count}
              onChange={(v) => setCount(typeof v === 'number' ? v : 1)}
              min={1}
              max={Math.max(1, Math.min(20, available))}
            />
            {count > available && (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  Only {available} available — you will get {available}, not {count}.
                </Text>
              </Alert>
            )}
            <Textarea
              label="Note (optional)"
              placeholder="Replacement for a code that wouldn't redeem"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              minRows={2}
              autosize
            />
          </>
        )}

        <Group justify="flex-end">
          <Button
            variant="subtle"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => assignMutation.mutate()}
            loading={assignMutation.isPending}
            disabled={!picked || poolEmpty}
          >
            Assign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export type { PickedUser };
